import React, { useState, useEffect } from 'react';
import { ref, onValue } from 'firebase/database';
import { db } from '../firebase/config';
import { FaSearch, FaBox, FaChartLine, FaDownload, FaFilter, FaTimes, FaCalendar, FaFileExcel } from 'react-icons/fa';
import './InventoryReport.css';
import * as XLSX from 'xlsx';

const InventoryReport = () => {
    const [products, setProducts] = useState([]);
    const [orders, setOrders] = useState([]);
    const [stockHistory, setStockHistory] = useState({});
    const [inventoryData, setInventoryData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('all');
    const [sortBy, setSortBy] = useState('name');

    // Date filtering states
    const [dateFilterType, setDateFilterType] = useState('all'); // 'all', 'day', 'month', 'year', 'custom'
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedMonth, setSelectedMonth] = useState('');
    const [selectedYear, setSelectedYear] = useState('');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');

    // Fetch products
    useEffect(() => {
        const productsRef = ref(db, 'HTAMS/company/products');
        const unsubscribe = onValue(productsRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const productsArray = Object.entries(data).map(([id, val]) => ({
                    id,
                    name: val.name || 'Unknown Product',
                    currentStock: Number(val.stock) || 0,
                    price: Number(val.price) || 0,
                    mrp: Number(val.mrp) || 0,
                    imageUrl: val.imageUrls && val.imageUrls.length > 0 ? val.imageUrls[0] : null,
                    createdAt: val.createdAt || null,
                }));
                setProducts(productsArray);
            } else {
                setProducts([]);
            }
        });

        return () => unsubscribe();
    }, []);

    // Fetch orders
    useEffect(() => {
        const ordersRef = ref(db, 'HTAMS/orders');
        const unsubscribe = onValue(ordersRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const ordersArray = Object.entries(data).map(([id, val]) => ({
                    id,
                    ...val,
                    orderDate: val.orderDate || new Date().toISOString(),
                    items: val.items || [],
                    status: val.status || 'pending',
                }));
                setOrders(ordersArray);
            } else {
                setOrders([]);
            }
        });

        return () => unsubscribe();
    }, []);

    // Fetch stock history
    useEffect(() => {
        const stockHistoryRef = ref(db, 'HTAMS/stockHistory');
        const unsubscribe = onValue(stockHistoryRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                setStockHistory(data);
                console.log('✅ Stock history loaded:', Object.keys(data).length, 'products');
            } else {
                setStockHistory({});
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // Calculate inventory data
    useEffect(() => {
        if (products.length === 0) {
            setInventoryData([]);
            setFilteredData([]);
            return;
        }

        const inventoryMap = new Map();

        // Initialize with current products
        products.forEach(product => {
            inventoryMap.set(product.id, {
                id: product.id,
                name: product.name,
                currentStock: product.currentStock,
                price: product.price,
                mrp: product.mrp,
                imageUrl: product.imageUrl,
                totalSales: 0,
                totalSalesAmount: 0,
                totalStockAdded: 0,
                transactions: [],
            });
        });

        // Add stock additions from history
        Object.entries(stockHistory).forEach(([productId, history]) => {
            if (inventoryMap.has(productId) && history) {
                const inventory = inventoryMap.get(productId);

                Object.values(history).forEach(record => {
                    if (record.type === 'addition') {
                        inventory.totalStockAdded += record.quantity || 0;

                        inventory.transactions.push({
                            type: 'addition',
                            quantity: record.quantity || 0,
                            date: record.date || new Date(record.timestamp).toISOString(),
                            timestamp: record.timestamp,
                            description: record.note || 'Stock Addition',
                        });
                    }
                });
            }
        });

        // Calculate sales from orders
        orders.forEach(order => {
            if (order.items && Array.isArray(order.items)) {
                order.items.forEach(item => {
                    // Check both 'id' and 'productId' for compatibility
                    const productId = item.productId || item.id;
                    if (productId && inventoryMap.has(productId)) {
                        const inventory = inventoryMap.get(productId);
                        inventory.totalSales += item.quantity || 0;
                        inventory.totalSalesAmount += (item.quantity || 0) * (item.unitPrice || item.mrp || item.price || 0);

                        inventory.transactions.push({
                            type: 'sale',
                            quantity: -(item.quantity || 0),
                            date: order.orderDate,
                            orderId: order.orderId || order.id,
                            amount: (item.quantity || 0) * (item.unitPrice || item.mrp || item.price || 0),
                            description: `Sale - Order ${(order.orderId || order.id || '').substring(0, 8)}...`,
                        });
                    }
                });
            }
        });


        // Calculate final inventory data
        const inventoryArray = Array.from(inventoryMap.values()).map(item => {
            const totalAdditions = item.totalStockAdded > 0
                ? item.totalStockAdded
                : item.currentStock + item.totalSales;

            const turnoverRate = totalAdditions > 0
                ? ((item.totalSales / totalAdditions) * 100).toFixed(1)
                : 0;

            item.transactions.sort((a, b) => {
                const dateA = new Date(a.date || a.timestamp);
                const dateB = new Date(b.date || b.timestamp);
                return dateB - dateA;
            });

            return {
                ...item,
                totalAdditions,
                turnoverRate,
                stockStatus: item.currentStock === 0 ? 'out-of-stock' :
                    item.currentStock <= 5 ? 'low-stock' : 'in-stock',
            };
        });

        setInventoryData(inventoryArray);
        setFilteredData(inventoryArray);
    }, [products, orders, stockHistory]);

    // Filter and search
    useEffect(() => {
        let filtered = [...inventoryData];

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(item =>
                item.name.toLowerCase().includes(query) ||
                item.id.toLowerCase().includes(query)
            );
        }

        if (filterStatus !== 'all') {
            filtered = filtered.filter(item => item.stockStatus === filterStatus);
        }

        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'name':
                    return a.name.localeCompare(b.name);
                case 'stock':
                    return b.currentStock - a.currentStock;
                case 'sales':
                    return b.totalSales - a.totalSales;
                case 'additions':
                    return b.totalAdditions - a.totalAdditions;
                default:
                    return 0;
            }
        });

        setFilteredData(filtered);
    }, [inventoryData, searchQuery, filterStatus, sortBy]);

    const getSummaryStats = () => {
        const totalProducts = inventoryData.length;
        const totalCurrentStock = inventoryData.reduce((sum, item) => sum + item.currentStock, 0);
        const totalSales = inventoryData.reduce((sum, item) => sum + item.totalSales, 0);
        const totalAdditions = inventoryData.reduce((sum, item) => sum + item.totalAdditions, 0);
        const totalSalesAmount = inventoryData.reduce((sum, item) => sum + item.totalSalesAmount, 0);
        const lowStockCount = inventoryData.filter(item => item.stockStatus === 'low-stock').length;
        const outOfStockCount = inventoryData.filter(item => item.stockStatus === 'out-of-stock').length;

        return {
            totalProducts,
            totalCurrentStock,
            totalSales,
            totalAdditions,
            totalSalesAmount,
            lowStockCount,
            outOfStockCount,
        };
    };

    const stats = getSummaryStats();

    const exportToCSV = () => {
        const headers = ['Product Name', 'Total Stock Added', 'Total Sales', 'Current Stock', 'Total Sales Amount'];
        const rows = filteredData.map(item => [
            item.name,
            item.totalAdditions,
            item.totalSales,
            item.currentStock,
            item.totalSalesAmount.toFixed(2),
        ]);

        // Create worksheet
        const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

        // Set column widths
        ws['!cols'] = [
            { wch: 30 }, // Product Name
            { wch: 18 }, // Total Stock Added
            { wch: 15 }, // Total Sales
            { wch: 15 }, // Current Stock
            { wch: 20 }, // Total Sales Amount
        ];

        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Stock Balance');

        // Export to Excel
        XLSX.writeFile(wb, `stock-balance-report-${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    if (loading) {
        return (
            <div className="inventory-report-loading">
                <div className="loading-spinner"></div>
                <p>Loading inventory data...</p>
            </div>
        );
    }

    return (
        <div className="inventory-report-container">
            <div className="inventory-report-header">
                <div className="header-title">
                    <FaBox className="header-icon" />
                    <h1>Inventory Management Report</h1>
                </div>
                <button className="export-btn" onClick={exportToCSV}>
                    <FaDownload /> Export CSV
                </button>
            </div>

            {/* Summary Cards */}
            <div className="summary-cards">
                <div className="summary-card">
                    <div className="card-icon products">
                        <FaBox />
                    </div>
                    <div className="card-content">
                        <h3>Total Products</h3>
                        <p className="card-value">{stats.totalProducts}</p>
                    </div>
                </div>

                <div className="summary-card">
                    <div className="card-icon additions">
                        <FaChartLine />
                    </div>
                    <div className="card-content">
                        <h3>Total Stock Added</h3>
                        <p className="card-value">{stats.totalAdditions}</p>
                    </div>
                </div>

                <div className="summary-card">
                    <div className="card-icon sales">
                        <FaChartLine />
                    </div>
                    <div className="card-content">
                        <h3>Total Sales</h3>
                        <p className="card-value">{stats.totalSales}</p>
                    </div>
                </div>

                <div className="summary-card">
                    <div className="card-icon current">
                        <FaBox />
                    </div>
                    <div className="card-content">
                        <h3>Current Stock</h3>
                        <p className="card-value">{stats.totalCurrentStock}</p>
                    </div>
                </div>

                <div className="summary-card">
                    <div className="card-icon revenue">
                        <FaChartLine />
                    </div>
                    <div className="card-content">
                        <h3>Total Revenue</h3>
                        <p className="card-value">₹{stats.totalSalesAmount.toLocaleString('en-IN')}</p>
                    </div>
                </div>

                <div className="summary-card warning">
                    <div className="card-icon low-stock">
                        <FaBox />
                    </div>
                    <div className="card-content">
                        <h3>Low Stock Items</h3>
                        <p className="card-value">{stats.lowStockCount}</p>
                    </div>
                </div>

                <div className="summary-card danger">
                    <div className="card-icon out-stock">
                        <FaBox />
                    </div>
                    <div className="card-content">
                        <h3>Out of Stock</h3>
                        <p className="card-value">{stats.outOfStockCount}</p>
                    </div>
                </div>
            </div>

            {/* Filters and Search */}
            <div className="filters-section">
                <div className="search-box">
                    <FaSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search by product name or ID..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                        <FaTimes
                            className="clear-search"
                            onClick={() => setSearchQuery('')}
                        />
                    )}
                </div>

                <div className="filter-controls">
                    <div className="filter-group">
                        <FaFilter className="filter-icon" />
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                        >
                            <option value="all">All Products</option>
                            <option value="in-stock">In Stock</option>
                            <option value="low-stock">Low Stock</option>
                            <option value="out-of-stock">Out of Stock</option>
                        </select>
                    </div>

                    <div className="filter-group">
                        <label>Sort by:</label>
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                        >
                            <option value="name">Name</option>
                            <option value="stock">Current Stock</option>
                            <option value="sales">Total Sales</option>
                            <option value="additions">Total Additions</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Inventory Table */}
            <div className="inventory-table-container">
                {filteredData.length === 0 ? (
                    <div className="no-data">
                        <FaBox className="no-data-icon" />
                        <p>No inventory data found</p>
                    </div>
                ) : (
                    <table className="inventory-table">
                        <thead>
                            <tr>
                                <th>Product</th>
                                <th>Total Stock Added</th>
                                <th>Total Sales</th>
                                <th>Current Stock</th>
                                <th>Sales Amount</th>
                                <th>Status</th>
                                <th>Recent Transactions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.map((item) => (
                                <tr key={item.id} className={`row-${item.stockStatus}`}>
                                    <td className="product-cell">
                                        <div className="product-info">
                                            {item.imageUrl ? (
                                                <img src={item.imageUrl} alt={item.name} className="product-image" />
                                            ) : (
                                                <div className="product-placeholder">
                                                    <FaBox />
                                                </div>
                                            )}
                                            <div className="product-details">
                                                <span className="product-name">{item.name}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="additions-cell">
                                        <span className="value-badge additions">{item.totalAdditions}</span>
                                    </td>
                                    <td className="sales-cell">
                                        <span className="value-badge sales">{item.totalSales}</span>
                                    </td>
                                    <td className="stock-cell">
                                        <span className={`value-badge ${item.stockStatus}`}>
                                            {item.currentStock}
                                        </span>
                                    </td>
                                    <td className="amount-cell">
                                        ₹{item.totalSalesAmount.toLocaleString('en-IN')}
                                    </td>
                                    <td className="status-cell">
                                        <span className={`status-badge ${item.stockStatus}`}>
                                            {item.stockStatus === 'in-stock' ? '✅ In Stock' :
                                                item.stockStatus === 'low-stock' ? '⚠️ Low Stock' :
                                                    '❌ Out of Stock'}
                                        </span>
                                    </td>
                                    <td className="transactions-cell">
                                        {item.transactions.length > 0 ? (
                                            <div className="transactions-preview">
                                                {item.transactions.slice(0, 3).map((transaction, index) => (
                                                    <div key={index} className="transaction-item">
                                                        <span className="transaction-type">
                                                            {transaction.quantity < 0 ? '📤 Sale' : '📥 Add'}
                                                        </span>
                                                        <span className={`transaction-qty ${transaction.quantity < 0 ? 'negative' : 'positive'}`}>
                                                            {transaction.quantity} units
                                                        </span>
                                                        <span className="transaction-date">
                                                            {formatDate(transaction.date)}
                                                        </span>
                                                    </div>
                                                ))}
                                                {item.transactions.length > 3 && (
                                                    <span className="more-transactions">
                                                        +{item.transactions.length - 3} more
                                                    </span>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="no-transactions">No transactions</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Results Count */}
            {filteredData.length > 0 && (
                <div className="results-count">
                    Showing {filteredData.length} of {inventoryData.length} products
                </div>
            )}
        </div>
    );
};

export default InventoryReport;
